import {
  getCmsIdentityToken,
  getRefreshToken,
  setIdToken,
  setRefreshToken,
  setSessionToken,
} from "./authToken";
import { refreshAccessToken } from "./services/iamService";
import { refreshTenantAuth } from "./tenantAuthRefresh";

type SessionExpiredHandler = () => void | Promise<void>;

let sessionExpiredHandler: SessionExpiredHandler | null = null;
let sessionExpiredNotified = false;

export function registerSessionExpiredHandler(
  handler: SessionExpiredHandler,
): void {
  sessionExpiredHandler = handler;
}

export function unregisterSessionExpiredHandler(): void {
  sessionExpiredHandler = null;
}

export function resetSessionExpiredNotification(): void {
  sessionExpiredNotified = false;
}

export async function notifySessionExpired(): Promise<void> {
  if (sessionExpiredNotified || !sessionExpiredHandler) {
    return;
  }
  sessionExpiredNotified = true;
  await sessionExpiredHandler();
}

/**
 * Refresh IAM tokens when available, then re-issue the tenant JWT.
 */
export async function tryRefreshAuth(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      const refreshed = await refreshAccessToken(refreshToken);
      if (refreshed.accessToken) {
        setSessionToken(refreshed.accessToken);
        if (refreshed.idToken) {
          setIdToken(refreshed.idToken);
        }
        if (refreshed.refreshToken) {
          setRefreshToken(refreshed.refreshToken);
        }
      }
    } catch {
      return false;
    }
  } else if (!getCmsIdentityToken()) {
    return false;
  }

  return refreshTenantAuth();
}

/** Attempt refresh after 401; force re-login when refresh fails. */
export async function handleUnauthorizedResponse(): Promise<boolean> {
  const refreshed = await tryRefreshAuth();
  if (!refreshed) {
    await notifySessionExpired();
    return false;
  }
  return true;
}
